import { CONTRACT_STATUS } from '@hetivo/hetivo-lib-types';
import type { ContractStatus } from '@hetivo/hetivo-lib-types';
import { z } from 'zod/v4';

// ─── Status filter ──────────────────────────────────────────────────────────────

/** The status filter: a concrete status, or `'all'` (every status but Archived — the server default). */
export type ContractStatusFilter = ContractStatus | 'all';

const CONTRACT_STATUS_VALUES: readonly string[] = Object.values(CONTRACT_STATUS);

/** Whether a raw string is a known {@link ContractStatus} — used to narrow a URL value without a cast. */
export function isContractStatus(value: string | undefined): value is ContractStatus {
  return value !== undefined && CONTRACT_STATUS_VALUES.includes(value);
}

// ─── URL search schema ──────────────────────────────────────────────────────────

/**
 * The contracts-list URL search-param schema (SWOOP-365). Lifting the search / status / page into the URL
 * (as Work orders does, SWOOP-326) lets a Back from a contract restore the list exactly as it was. Every
 * field is optional and `.catch(undefined)`, so a hand-edited or stale URL degrades to the defaults
 * instead of throwing, and a plain navigation to `/leasing/contracts` needs no search object. `status` is
 * validated as a bare string and narrowed in the hook, so an unknown status token simply falls back to
 * "all" rather than rejecting the whole URL.
 */
export const contractsListSearchSchema = z.object({
  page: z.number().int().positive().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  status: z.string().optional().catch(undefined),
});

export type ContractsListSearch = z.infer<typeof contractsListSearchSchema>;

/**
 * The contract page's tabs, in order. `lines` is the Vehicle lines tab, labelled Agreements once the contract is
 * signed.
 */
export const CONTRACT_PAGE_TABS = ['overview', 'lines', 'quotations', 'contract'] as const;

export type ContractPageTab = (typeof CONTRACT_PAGE_TABS)[number];

/**
 * The contract page's URL search: the list's search / status / page (so Back restores the list) plus the tab to
 * open, so the agreement page's Back lands on the Agreements tab rather than Overview. An unknown `tab` falls back
 * to Overview instead of throwing.
 */
export const contractPageSearchSchema = contractsListSearchSchema.extend({
  tab: z.enum(CONTRACT_PAGE_TABS).optional().catch(undefined),
});

export type ContractPageSearch = z.infer<typeof contractPageSearchSchema>;

/**
 * The agreement page's URL search (SWOOP-414): the list's search / status / page (so Back restores the list)
 * plus `edit`, which opens the page in modify mode. A hand-edited or stale `edit` degrades to `undefined`
 * (view mode) rather than throwing; the page itself drops `edit` when the viewer lacks the modify grant or the
 * agreement is not On lease.
 */
/**
 * The agreement page's tabs, in order. `overview` is the agreement body (terms, vehicle, customer, documents);
 * `billing` is the not-yet-built Billing schedule, which shows the coming-soon placeholder. Overview is the
 * default and stays out of the URL.
 */
export const AGREEMENT_PAGE_TABS = ['overview', 'billing'] as const;

export type AgreementPageTab = (typeof AGREEMENT_PAGE_TABS)[number];

export const agreementPageSearchSchema = contractsListSearchSchema.extend({
  edit: z.boolean().optional().catch(undefined),
  tab: z.enum(AGREEMENT_PAGE_TABS).optional().catch(undefined),
});

export type AgreementPageSearch = z.infer<typeof agreementPageSearchSchema>;

// ─── Conversions ────────────────────────────────────────────────────────────────

/** The status filter the list shows for a URL `status` value: the known status, or `'all'` otherwise. */
export function searchToStatusFilter(search: ContractsListSearch): ContractStatusFilter {
  return isContractStatus(search.status) ? search.status : 'all';
}

/** The URL `status` field for a filter value: the status itself, or `undefined` for `'all'` (kept out of the URL). */
export function statusFilterToSearch(status: ContractStatusFilter): ContractStatus | undefined {
  return status === 'all' ? undefined : status;
}
